'use client';

import { Banner } from '@flipster/apis/v1/banner/types';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { Button } from '@flipster/react/design-system';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, vstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { MainEvent } from '@flipster/utils/segment/events/main';

import { ContinueWithApple, ContinueWithGoogle } from '../continue-with-button';
import { DownloadAppButton } from '../download-app-button';
import EmailTextFieldCTA from './EmailTextFieldCTA';
import { RollingBanner } from './rolling-banner';

interface Props {
  heroCarousel?: Banner[];
}

const BaseHero = ({ heroCarousel }: Props) => {
  const isMounted = useMounted();
  const { isLoggedIn } = useAuth();

  return (
    <section
      className={flex({
        w: 'auto',
        maxW: { tablet: 'desktopMaxWidth' },
        mx: { base: 0, tablet: 16, smallDesktop: 'auto' },
        flexDirection: { base: 'column', tablet: 'row' },
        alignItems: 'center',
        justifyContent: 'space-between',
        pt: { base: 24, tablet: 48 },
        pb: { base: 0, tablet: 48 },
        gap: 24,
      })}
    >
      <div className={css({ w: '100%', maxW: { tablet: 470 }, px: { tabletDown: 16 } })}>
        <h1
          className={css({
            textStyle: 'Display/XXL',
            color: 'content.default.level-5',
            textAlign: { tabletDown: 'center' },
          })}
        >
          Zero Spread
          <br />
          <span className={css({ color: 'content.accent.info' })}>Perps.</span>
        </h1>
        <p
          className={css({
            textStyle: 'Title/S',
            color: 'content.default.level-5',
            mt: { base: 4, tablet: 16 },
            textAlign: { tabletDown: 'center' },
          })}
        >
          One market price, major crypto pairs
        </p>
        {isMounted && isLoggedIn ? (
          <div
            className={vstack({
              w: '100%',
              maxW: { tablet: 180 },
              gap: { base: 12, tablet: 20 },
              pt: { base: 24, tablet: 48 },
              alignItems: 'flex-start',
            })}
          >
            <Button
              hierarchy='primary'
              color='accent'
              size='large'
              as='a'
              href='/trade/latest'
              label='Trade now'
              onClick={() => segment.track(MainEvent.MainPageTradeNowClick)}
              css={{ minW: { base: '100%', tablet: 120 } }}
            />
            <DownloadAppButton />
          </div>
        ) : (
          <div className={css({ w: '100%', maxW: { tablet: 470 }, pt: { base: 24, tablet: 48 } })}>
            <div className={css({ mb: { base: 12, tablet: 20 } })}>
              <EmailTextFieldCTA />
            </div>
            <div
              className={flex({
                justify: 'space-between',
                flexDirection: { base: 'column', tablet: 'row' },
                gap: 24,
              })}
            >
              <div
                className={flex({
                  display: { tabletDown: 'none', tablet: 'flex' },
                  align: { tablet: 'center' },
                  flexDirection: { base: 'column', tablet: 'row' },
                  textStyle: 'Text/M/Strong',
                  color: 'content.default.level-2',
                  gap: 8,
                })}
              >
                <p>Continue with</p>
                <div className={hstack({ gap: 8 })}>
                  <ContinueWithGoogle />
                  <ContinueWithApple />
                </div>
              </div>
              <DownloadAppButton />
            </div>
          </div>
        )}
      </div>
      <RollingBanner
        items={
          heroCarousel?.map((item) => ({
            ...item,
            desktopImage: `${item.desktopImage}?fm=webp&q=80`,
            mobileImage: `${item.mobileImage}?fm=webp&q=80`,
          })) ?? []
        }
      />
    </section>
  );
};

export default BaseHero;
